import { DashboardDefinition } from './dashboard-definition.model';
import { COMPARISON_FOUR_GRID_DEFINITIONS } from '../comparison/_shared/dashboard-comparison-four-grid/dashboard-comparison-four-grid.definitions';
import { COMPARISON_INDUSTRIES_DEFINITIONS } from '../comparison/_shared/dashboard-comparison-industries/dashboard-comparison-industries.definitions';
import { COMPARISON_STRUCTURE_DEFINITIONS } from '../comparison/_shared/dashboard-comparison-structure/dashboard-comparison-structure.definitions';
import { DOSSIER_STATES_DEVELOPMENT_DEFINITIONS } from '../dossiers/_shared/dashboard-dossier-states-development/dashboard-dossier-states-development.definitions';
import { DOSSIER_STATES_INFO_DEFINITIONS } from '../dossiers/_shared/dashboard-dossier-states-info/dashboard-dossier-states-info.definitions';
import { DOSSIER_STATES_MARKET_DEFINITIONS } from '../dossiers/_shared/dashboard-dossier-states-market/dashboard-dossier-states-market.definitions';
import { DOSSIER_STATES_ONION_DEFINITIONS } from '../dossiers/_shared/dashboard-dossier-states-onion/dashboard-dossier-states-onion.definitions';
import { DOSSIER_STATES_OVERVIEW_DEFINITIONS } from '../dossiers/_shared/dashboard-dossier-states-overview/dashboard-dossier-states-overview.definitions';
import { FORECAST_COMPARISON_DEFINITIONS } from '../forecasts/_shared/dashboard-forecast-comparison/dashboard-forecast-comparison.definitions';
import { FORECAST_STRUCTURE_DEFINITIONS } from '../forecasts/_shared/dashboard-forecast-structure/dashboard-forecast-structure.definitions';
import { INDEX_RANKING_DEFINITIONS } from '../index/_shared/dashboard-index-ranking/dashboard-index-ranking.definitions';
import { INDEX_RANKING_DEVELOPMENT_DEFINITIONS } from '../index/_shared/dashboard-index-ranking-development/dashboard-index-ranking-development.definitions';
import { RANKING_DEFINITIONS } from '../rankings/_shared/dashboard-ranking/dashboard-ranking.definitions';
import { RANKING_MULTI_COLUMN_DEFINITIONS } from '../rankings/_shared/dashboard-ranking-multi-column/dashboard-ranking-multi-column.definitions';
import { TIME_SERIES_BAR_DEFINITIONS } from '../time-series/_shared/dashboard-time-series-bar/dashboard-time-series-bar.definitions';

/** A built dashboard and the route path it is served at. */
export interface RegisteredDashboard {
  definition: DashboardDefinition<any>;
  /** `dashboardId`, or `dashboardId/variant` for analysis variants (dossiers, forecast comparison). */
  path: string;
}

export interface DashboardRegistry {
  readonly entries: readonly RegisteredDashboard[];
  /**
   * The definition of a dashboard id. Ids repeat across analysis variants (the five dossier
   * families share `1x1x3`), so narrow by the shell (saved slices) or the variant (routes);
   * without either, the first match wins.
   */
  get(dashboardId: string, by?: { shellId?: string; variant?: string }): DashboardDefinition<any> | undefined;
}

export function dashboardPathOf(definition: Pick<DashboardDefinition<unknown>, 'dashboardId' | 'variant'>): string {
  return definition.variant ? `${definition.dashboardId}/${definition.variant}` : definition.dashboardId;
}

export function createDashboardRegistry(families: readonly (readonly DashboardDefinition<any>[])[]): DashboardRegistry {
  const entries: RegisteredDashboard[] = families.flat().map((definition) => ({ definition, path: dashboardPathOf(definition) }));
  return {
    entries,
    get: (dashboardId, by = {}) =>
      entries.find(
        ({ definition }) =>
          definition.dashboardId === dashboardId &&
          (by.shellId === undefined || definition.shellId === by.shellId) &&
          (by.variant === undefined || definition.variant === by.variant),
      )?.definition,
  };
}

/** Every dashboard built from a family catalog. Hand-written legacy dashboards are not listed. */
export const DASHBOARD_REGISTRY = createDashboardRegistry([
  DOSSIER_STATES_INFO_DEFINITIONS,
  DOSSIER_STATES_OVERVIEW_DEFINITIONS,
  DOSSIER_STATES_ONION_DEFINITIONS,
  DOSSIER_STATES_MARKET_DEFINITIONS,
  DOSSIER_STATES_DEVELOPMENT_DEFINITIONS,
  COMPARISON_FOUR_GRID_DEFINITIONS,
  COMPARISON_INDUSTRIES_DEFINITIONS,
  COMPARISON_STRUCTURE_DEFINITIONS,
  INDEX_RANKING_DEFINITIONS,
  INDEX_RANKING_DEVELOPMENT_DEFINITIONS,
  FORECAST_COMPARISON_DEFINITIONS,
  FORECAST_STRUCTURE_DEFINITIONS,
  TIME_SERIES_BAR_DEFINITIONS,
  RANKING_DEFINITIONS,
  RANKING_MULTI_COLUMN_DEFINITIONS,
]);
